import type { ButtonProps } from './Button';
import type { Ref } from 'react';

import { forwardRef } from 'react';
import { css, styled } from 'styled-components';

import { ButtonContent, DefaultButtonSC } from './Button';

type Props = ButtonProps & {
  colorVariant?: 'blue' | 'green' | 'aqua';
};

export const secondaryButtonColorsGreen = css`
  background: ${(props) => props.theme.colors.transparent};
  border-color: ${(props) => props.theme.colors.emeraldGreen[100]};
  color: ${(props) => props.theme.colors.emeraldGreen[100]};

  &:hover {
    background: ${(props) => props.theme.colors.emeraldGreen[100]};
    border-color: ${(props) => props.theme.colors.emeraldGreen[100]};
    color: ${(props) => props.theme.colors.white};
  }

  &:active {
    color: ${(props) => props.theme.colors.shadows.white[80]};
  }

  &[disabled],
  &:disabled {
    background: ${(props) => props.theme.colors.transparent};
    border-color: ${(props) => props.theme.colors.shadows.oxfordBlue[30]};
    color: ${(props) => props.theme.colors.shadows.oxfordBlue[80]};
  }
`;

export const secondaryButtonColorsBlue = css`
  background: ${(props) => props.theme.colors.transparent};
  border-color: ${(props) => props.theme.colors.aeroBlue[100]};
  color: ${(props) => props.theme.colors.aeroBlue[100]};

  &:hover {
    background: ${(props) => props.theme.colors.aeroBlue[100]};
    border-color: ${(props) => props.theme.colors.aeroBlue[100]};
    color: ${(props) => props.theme.colors.white};
  }

  &:active {
    color: ${(props) => props.theme.colors.shadows.white[80]};
  }

  &[disabled],
  &:disabled {
    background: ${(props) => props.theme.colors.transparent};
    border-color: ${(props) => props.theme.colors.shadows.oxfordBlue[30]};
    color: ${(props) => props.theme.colors.shadows.oxfordBlue[80]};
  }

  &:focus {
    box-shadow: 0 0 0 0.1875rem ${(props) => props.theme.colors.aeroBlue[50]};
  }
`;

export const secondaryButtonColorsAqua = css`
  background: ${(props) => props.theme.colors.transparent};
  border-color: ${(props) => props.theme.colors.tiffanyGreen[100]};
  color: ${(props) => props.theme.colors.tiffanyGreen[100]};

  &:hover {
    background: ${(props) => props.theme.colors.tiffanyGreen[100]};
    border-color: ${(props) => props.theme.colors.tiffanyGreen[100]};
    color: ${(props) => props.theme.colors.white};
  }

  &:active {
    color: ${(props) => props.theme.colors.shadows.white[80]};
  }

  &[disabled],
  &:disabled {
    background: ${(props) => props.theme.colors.transparent};
    border-color: ${(props) => props.theme.colors.shadows.oxfordBlue[30]};
    color: ${(props) => props.theme.colors.shadows.oxfordBlue[80]};
  }

  &:focus {
    box-shadow: 0 0 0 0.1875rem ${(props) => props.theme.colors.tiffanyGreen[50]};
  }
`;

const SecondaryButtonSC = styled(DefaultButtonSC)<{ $colorVariant?: 'blue' | 'green' | 'aqua' }>`
  ${(props) => {
    switch (props.$colorVariant) {
      case 'blue':
        return secondaryButtonColorsBlue;
      case 'aqua':
        return secondaryButtonColorsAqua;
      default:
        return secondaryButtonColorsGreen;
    }
  }}

  border: 0.125rem solid;
  border-radius: 1.625rem;
`;

export const SecondaryButton = forwardRef(
  ({ children, colorVariant, leftIcon, rightIcon, size, ...props }: Props, ref: Ref<HTMLButtonElement>) => {
    return (
      <SecondaryButtonSC ref={ref} $colorVariant={colorVariant} $size={size} {...props}>
        <ButtonContent leftIcon={leftIcon} rightIcon={rightIcon}>
          {children}
        </ButtonContent>
      </SecondaryButtonSC>
    );
  },
);
